import { render } from 'preact';
import { useState, useEffect } from 'preact/hooks';

// --- GID-based collection fetch (no handle lookup) ---
const COLLECTION_BY_GID_QUERY = `
    query($id: ID!) {
        collection: node(id: $id) {
            ... on Collection {
                id
                title
                handle
                description
                image {
                    src
                    altText
                }
                products(first: 50) {
                    edges {
                        node {
                            id
                            title
                            handle
                            description
                            tags
                            productType
                            subbrand_prefix: metafield(namespace: "cirkul", key: "subbrand_prefix" ) {
                                value
                            }
                            isOutOfStock: metafield(namespace: "cirkul", key: "out_of_stock" ) {
                                value
                            }
                            launchTimestamp: metafield(namespace: "cirkul", key: "launch_timestamp" ) {
                                value
                            }
                            images(first: 1) {
                                edges {
                                    node {
                                        src
                                    }
                                }
                            }
                        }
                    }
                }
                metafield(namespace: "custom", key: "merch_swiper_promos") {
                    value
                    reference {
                        ... on Metaobject {
                            id
                            type
                            fields {
                                key
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;

// --- Handle-based collection fetch (no GID lookup) ---
const COLLECTION_BY_HANDLE_QUERY = `
    query($handle: String!) {
        collectionByHandle(handle: $handle) {
            id
            title
            handle
            description
            descriptionHtml
            image {
                src
                altText
            }
            products(first: 50) {
                edges {
                    node {
                        id
                        title
                        handle
                        description
                        tags
                        productType
                        subbrand_prefix: metafield(namespace: "cirkul", key: "subbrand_prefix" ) {
                            value
                        }
                        isOutOfStock: metafield(namespace: "cirkul", key: "out_of_stock" ) {
                            value
                        }
                        launchTimestamp: metafield(namespace: "cirkul", key: "launch_timestamp" ) {
                            value
                        }
                        images(first: 1) {
                            edges {
                                node {
                                    src
                                }
                            }
                        }
                    }
                }
            }
            metafield(namespace: "custom", key: "merch_swiper_promos") {
                value
                reference {
                    ... on Metaobject {
                        id
                        type
                        fields {
                            key
                            value
                        }
                    }
                }
            }
            # Add hero metafields
            hero_background_mobile: metafield(namespace: "hero", key: "background_mobile") {
                value
                reference {
                    ... on MediaImage {
                        image {
                            url
                        }
                    }
                }
            }
            hero_background_desktop: metafield(namespace: "hero", key: "background_desktop") {
                value
                reference {
                    ... on MediaImage {
                        image {
                            url
                        }
                    }
                }
            }
            hero_logo_mobile: metafield(namespace: "hero", key: "logo_mobile") {
                value
                reference {
                    ... on MediaImage {
                        image {
                            url
                        }
                    }
                }
            }
            hero_logo_desktop: metafield(namespace: "hero", key: "logo_desktop") {
                value
                reference {
                    ... on MediaImage {
                        image {
                            url
                        }
                    }
                }
            }
            hero_family_mobile: metafield(namespace: "hero", key: "family_mobile") {
                value
                reference {
                    ... on MediaImage {
                        image {
                            url
                        }
                    }
                }
            }
            hero_family_desktop: metafield(namespace: "hero", key: "family_desktop") {
                value
                reference {
                    ... on MediaImage {
                        image {
                            url
                        }
                    }
                }
            }
        }
    }
`;

const STOREFRONT_ACCESS_TOKEN =
    window?.SHOPIFY_STOREFRONT_ACCESS_TOKEN ||
    '722b17daa2fefc4412d3f554625ded96'; // fallback for dev

// --- Fetch by GID only ---
export async function fetchCollectionByGid(gid) {
    if (!gid) throw new Error("No collection GID provided");
    const response = await fetch('/api/graphql', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({
            query: COLLECTION_BY_GID_QUERY,
            variables: { id: gid },
        }),
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Shopify Storefront API error: ${response.status}`);
    }

    const data = await response.json();
    const collection = data.data && data.data.collection;
    if (!collection) return null;

    // Flatten products and metafields for easier use
    const products = (collection.products?.edges || []).map(({ node }) => {
        const metafields = {};
        if (Array.isArray(node.metafields)) {
            node.metafields.forEach(mf => {
                metafields[mf.key] = mf.value;
            });
        }
        return {
            ...node,
            metafields,
            image: node.images?.edges?.[0]?.node?.src || null,
        };
    });

    // Parse merch_swiper_promos metaobject(s)
    let merchSwiperPromos = [];
    const merchField = collection.metafield;
    if (merchField && merchField.reference) {
        if (Array.isArray(merchField.reference)) {
            merchSwiperPromos = merchField.reference.map(ref => ({
                id: ref.id,
                type: ref.type,
                fields: ref.fields,
            }));
        } else {
            merchSwiperPromos = [{
                id: merchField.reference.id,
                type: merchField.reference.type,
                fields: merchField.reference.fields,
            }];
        }
    }

    return {
        ...collection,
        products,
        merchSwiperPromos,
    };
}

// --- Fetch by handle only ---
export async function fetchCollectionByHandle(handle) {
    if (!handle) throw new Error("No collection handle provided");
    const response = await fetch('/api/graphql', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({
            query: COLLECTION_BY_HANDLE_QUERY,
            variables: { handle },
        }),
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Shopify Storefront API error: ${response.status}`);
    }

    const data = await response.json();
    const collection = data.data && data.data.collectionByHandle;
    if (!collection) return null;

    // Flatten products and metafields for easier use
    const products = (collection.products?.edges || []).map(({ node }) => {
        const metafields = {};
        if (Array.isArray(node.metafields)) {
            node.metafields.forEach(mf => {
                metafields[mf.key] = mf.value;
            });
        }
        return {
            ...node,
            metafields,
            image: node.images?.edges?.[0]?.node?.src || null,
        };
    });

    // Parse merch_swiper_promos metaobject(s)
    let merchSwiperPromos = [];
    const merchField = collection.metafield;
    if (merchField && merchField.reference) {
        if (Array.isArray(merchField.reference)) {
            merchSwiperPromos = merchField.reference.map(ref => ({
                id: ref.id,
                type: ref.type,
                fields: ref.fields,
            }));
        } else {
            merchSwiperPromos = [{
                id: merchField.reference.id,
                type: merchField.reference.type,
                fields: merchField.reference.fields,
            }];
        }
    }

    // Extract hero metafields
    const heroMetafields = {};
    
    // Helper to extract image URL from metafield
    const extractImageUrl = (metafield) => {
        if (!metafield) return null;
        if (metafield.value && typeof metafield.value === 'string') {
            return metafield.value;
        }
        if (metafield.reference?.image?.url) {
            return metafield.reference.image.url;
        }
        return null;
    };

    heroMetafields.hero_background_mobile = extractImageUrl(collection.hero_background_mobile);
    heroMetafields.hero_background_desktop = extractImageUrl(collection.hero_background_desktop);
    heroMetafields.hero_logo_mobile = extractImageUrl(collection.hero_logo_mobile);
    heroMetafields.hero_logo_desktop = extractImageUrl(collection.hero_logo_desktop);
    heroMetafields.hero_family_mobile = extractImageUrl(collection.hero_family_mobile);
    heroMetafields.hero_family_desktop = extractImageUrl(collection.hero_family_desktop);

    return {
        ...collection,
        products,
        merchSwiperPromos,
        metafields: heroMetafields,
    };
}

// --- Example usage in a component (for reference only) ---
// import { fetchCollectionByGid, fetchCollectionByHandle } from './CollectionQL';
// const collection = await fetchCollectionByGid('gid://shopify/Collection/12345');
// const collection = await fetchCollectionByHandle('my-collection-handle');

export function CollectionQL({ handle, classes, placeholder }) {
    const [collection, setCollection] = useState(null);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState(handle || '');

    useEffect(() => {
        if (query) {
            setLoading(true);
            // --- FIX: Use the correct fetch function based on input ---
            const isGid = typeof query === "string" && query.startsWith("gid://shopify/Collection/");
            const fetchFn = isGid ? fetchCollectionByGid : fetchCollectionByHandle;
            fetchFn(query)
                .then((coll) => {
                    setCollection(coll);
                    setLoading(false);
                })
                .catch(() => {
                    setCollection(null);
                    setLoading(false);
                });
        } else {
            setCollection(null);
        }
    }, [query]);

    return (
        <div className={classes}>
            <input
                type="text"
                className="tw-w-full tw-mb-4 tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-4"
                placeholder={placeholder || 'Enter collection handle...'}
                value={query}
                onInput={e => setQuery(e.target.value)}
            />
            {loading && <div className="tw-text-gray-500">Loading...</div>}
            {collection && (
                <div>
                    <h2>{collection.title}</h2>
                    <div dangerouslySetInnerHTML={{ __html: collection.descriptionHtml }} />
                    {collection.image && (
                        <img src={collection.image.src} alt={collection.image.altText || collection.title} style={{ maxWidth: 200 }} />
                    )}
                    <h3>Products</h3>
                    <ul>
                        {collection.products.map(product => (
                            <li key={product.id}>
                                {product.title}
                                {product.image && (
                                    <img src={product.image} alt={product.title} style={{ maxWidth: 60, marginLeft: 8 }} />
                                )}
                            </li>
                        ))}
                    </ul>
                    {collection.merchSwiperPromos.length > 0 && (
                        <div>
                            <h3>Merch Swiper Promos</h3>
                            <ul>
                                {collection.merchSwiperPromos.map(promo => (
                                    <li key={promo.id}>
                                        <strong>{promo.type}</strong>
                                        <ul>
                                            {promo.fields.map(f => (
                                                <li key={f.key}>{f.key}: {f.value}</li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export async function Root(element, data) {
    const { handle, classes, placeholder } = data;
    render(<CollectionQL handle={handle} classes={classes} placeholder={placeholder} />, element);
}
